import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Building2, DollarSign, CheckCircle2, Download, Sparkles } from 'lucide-react';

interface GenerateurPropositionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GenerateurPropositionModal({ isOpen, onClose }: GenerateurPropositionModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
                  <FileText className="w-8 h-8" />
                  Générateur de Propositions Commerciales
                </h2>
                <p className="text-white/90 text-lg">Créez des propositions pro en 3 clics avec l'IA 🚀</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8">
            {/* Coming Soon Notice */}
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-400 rounded-xl p-8 mb-8 text-center">
              <Sparkles className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-3">🚧 En Développement Actif 🚧</h3>
              <p className="text-lg text-gray-700 mb-4">
                Ce module sera disponible très bientôt! Notre équipe travaille à créer 
                un générateur de propositions ultra-puissant.
              </p>
              <div className="inline-block bg-yellow-200 text-yellow-900 px-4 py-2 rounded-full font-bold">
                Lancement prévu: Q2 2025
              </div>
            </div>

            {/* Features Preview */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">✨ Fonctionnalités Planifiées</h3>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-500 text-white rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900">Questionnaire Intelligent</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Nom client, secteur, taille</li>
                    <li>• Besoins spécifiques identifiés</li>
                    <li>• Budget disponible</li>
                    <li>• Timeline souhaitée</li>
                    <li>• Pain points principaux</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-purple-500 text-white rounded-lg flex items-center justify-center">
                      <Sparkles className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900">Génération IA Personnalisée</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Résumé exécutif adapté</li>
                    <li>• Solutions recommandées</li>
                    <li>• Étapes d'implémentation</li>
                    <li>• Bénéfices mesurables (ROI)</li>
                    <li>• Cas clients similaires</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-500 text-white rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900">Pricing Automatique</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Calcul selon complexité</li>
                    <li>• Options Essentiel/Pro/Expert</li>
                    <li>• Breakdown détaillé</li>
                    <li>• Options paiement flexible</li>
                    <li>• Conditions et garanties</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center">
                      <Download className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900">Export Multi-Format</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• PDF branded BZ</li>
                    <li>• PowerPoint éditable</li>
                    <li>• Word (backup)</li>
                    <li>• Envoi email direct</li>
                    <li>• Tracking ouvertures</li>
                  </ul>
                </div>
              </div>

              {/* Structure Preview */}
              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">📄 Structure de la Proposition (10-15 pages)</h4>
                
                <div className="space-y-3">
                  {[
                    { title: '1. Page Couverture', desc: 'Logo BZ, client, date, branding professionnel' },
                    { title: '2. Résumé Exécutif', desc: 'Contexte, enjeux, solution recommandée (1 page)' },
                    { title: '3. Compréhension des Besoins', desc: 'Reformulation pain points client (2 pages)' },
                    { title: '4. Solution Proposée', desc: 'Approche, méthodologie, livrables (3 pages)' },
                    { title: '5. Équipe & Expertise', desc: 'Profils consultants, certifications (1 page)' },
                    { title: '6. Timeline & Phases', desc: 'Roadmap visuel, milestones (1 page)' },
                    { title: '7. Investissement', desc: 'Pricing détaillé, options, paiement (2 pages)' },
                    { title: '8. Cas Clients & ROI', desc: 'Testimonials, résultats mesurables (1 page)' },
                    { title: '9. Conditions & Garanties', desc: 'Termes, confidentialité, support (1 page)' },
                    { title: '10. Signature & Acceptation', desc: 'Page signature électronique' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-bold text-sm text-gray-900">{item.title}</p>
                        <p className="text-xs text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process Preview */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-400 rounded-xl p-6">
                <h4 className="text-xl font-bold text-indigo-900 mb-4">🚀 Processus (5 Minutes!)</h4>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border-2 border-indigo-300 text-center">
                    <div className="w-12 h-12 bg-indigo-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                      1
                    </div>
                    <p className="font-bold text-gray-900 mb-1">Remplir Form</p>
                    <p className="text-xs text-gray-600">2 min - Infos client de base</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border-2 border-indigo-300 text-center">
                    <div className="w-12 h-12 bg-indigo-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                      2
                    </div>
                    <p className="font-bold text-gray-900 mb-1">IA Génère</p>
                    <p className="text-xs text-gray-600">2 min - Proposition complète</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border-2 border-indigo-300 text-center">
                    <div className="w-12 h-12 bg-indigo-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                      3
                    </div>
                    <p className="font-bold text-gray-900 mb-1">Réviser & Envoyer</p>
                    <p className="text-xs text-gray-600">1 min - Ajustements, export, send</p>
                  </div>
                </div>
              </div>

              {/* Templates */}
              <div className="bg-white border-2 border-teal-400 rounded-xl p-6">
                <h4 className="text-xl font-bold text-teal-900 mb-4">📚 Templates Pré-Configurés</h4>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-300">
                    <FileText className="w-8 h-8 text-teal-600 mb-2" />
                    <p className="font-bold text-gray-900 text-sm mb-1">Diagnostic TI</p>
                    <p className="text-xs text-gray-600">Bilan de santé + Audit sécurité</p>
                  </div>
                  
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-300">
                    <FileText className="w-8 h-8 text-teal-600 mb-2" />
                    <p className="font-bold text-gray-900 text-sm mb-1">Services Gérés MSP</p>
                    <p className="text-xs text-gray-600">Infogérance, support, monitoring</p>
                  </div>
                  
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-300">
                    <FileText className="w-8 h-8 text-teal-600 mb-2" />
                    <p className="font-bold text-gray-900 text-sm mb-1">Projet Custom</p>
                    <p className="text-xs text-gray-600">Migration, intégration, dev</p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl p-6">
                <h4 className="text-xl font-bold mb-4 text-center">💪 Impact Attendu</h4>
                
                <div className="grid md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-4xl font-bold mb-1">90%</p>
                    <p className="text-sm opacity-90">Temps économisé vs manuel</p>
                  </div>
                  <div>
                    <p className="text-4xl font-bold mb-1">2x</p>
                    <p className="text-sm opacity-90">Taux conversion propositions</p>
                  </div>
                  <div>
                    <p className="text-4xl font-bold mb-1">100%</p>
                    <p className="text-sm opacity-90">Cohérence branding BZ</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 border-t border-gray-300 p-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <p className="font-bold">📬 Vous voulez être notifié au lancement?</p>
                <p>Contactez l'équipe dev pour être en beta test!</p>
              </div>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all"
              >
                Compris! 👍
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

